import { TODAY } from 'constants/dateLimits';
import { DateModel, DateRangeModel } from 'forms/shared/types';
import { getSafeDateTime } from 'helpers/date';
import { SafeDateTime, ValidSafeDateTime } from 'helpers/types';
import { DateObject } from 'luxon';

export interface BaseSafeDateTimeRange {
  from: SafeDateTime
  to: SafeDateTime
  valid?: boolean
  present?: boolean
}

export interface InvalidSafeDateTimeRange extends BaseSafeDateTimeRange {
  from: SafeDateTime
  to: SafeDateTime
  valid: false
  present?: boolean
}

export interface ValidSafeDateTimeRange extends BaseSafeDateTimeRange {
  from: ValidSafeDateTime
  to: ValidSafeDateTime
  valid: true
  present?: boolean
}

export type SafeDateTimeRange = ValidSafeDateTimeRange | InvalidSafeDateTimeRange

/**
 * Type Guard: Converts BaseSafeDateTimeRange to ValidSafeDateTimeRange when valid.
 */
export const isValidSafeDateTimeRange = (
  dateRange: BaseSafeDateTimeRange
): dateRange is ValidSafeDateTimeRange => {
  if (dateRange.from.valid === false) { return false; }
  if (dateRange.present === true) { return true; }
  if (dateRange.to.valid === true) { return true; }

  return false;
};

/**
 * Possible types for parseSafeDateTimeRange
 */
type ParseSafeDateTimeRangeTypes =
  | {
      from?: Partial<DateModel> | Partial<DateObject>
      to?: Partial<DateModel> | Partial<DateObject>
      present?: boolean
    }
  | Partial<DateRangeModel>
  | undefined
  | null

/**
 * Parses raw DateTimeRange to SafeDateTimeRange
 */
export const parseSafeDateTimeRange = (
  dateRanges: ParseSafeDateTimeRangeTypes
): SafeDateTimeRange => {
  const to = getSafeDateTime(dateRanges?.to);
  const from = getSafeDateTime(dateRanges?.from);
  const context = {
    from,
    to: dateRanges?.present === true ? getSafeDateTime(TODAY) : to,
    present: dateRanges?.present,
  };

  if (isValidSafeDateTimeRange(context)) {
    context.valid = true;

    return context;
  }

  const invalid: InvalidSafeDateTimeRange = {
    from,
    to,
    valid: false,
  };

  return invalid;
};

/**
 * This will take a DateRange array and output only valid date ranges. If none
 * are valid, array is empty.
 */
export const validateDateRanges = (
  ranges: DeepSafePartial<DateRangeModel[]>
): ValidSafeDateTimeRange[] => {
  const context: SafeDateTimeRange[] = ranges.map(parseSafeDateTimeRange);

  const validRanges: ValidSafeDateTimeRange[] = context.filter(isValidSafeDateTimeRange);

  return validRanges;
};
